import { supabase } from './supabaseClient';

/**
 * Ziehen einer Belegnummer (Angebot, Auftragsbestaetigung, Rechnung).
 *
 * Eine Stelle fuer alle Aufrufer, weil die RPC in zwei Fassungen im Feld
 * steht: der alte einargumentige Stand (next_document_number(p_kind)) und der
 * neue zweiargumentige aus supabase/numbering_jjmm.sql
 * (next_document_number(p_kind, p_type_digit)). Das SQL wird vom Betreiber von
 * Hand eingespielt — bis dahin muss der Client unveraendert weiterlaufen.
 *
 * PostgREST loest eine RPC ueber die Namen der uebergebenen Argumente auf:
 * p_type_digit an die alte Funktion zu schicken, ergibt keinen ignorierten
 * Parameter, sondern PGRST202 ("Could not find the function ... in the schema
 * cache"). Deshalb wird der Fehler abgefangen und der Aufruf einmal
 * einargumentig wiederholt.
 */

/** 1 = SiGeKo, 2 = SiFa, 3 = Sonstige — wie die Typziffer der Projektnummer. */
export type TypeDigit = '1' | '2' | '3';

const HINT =
    'Belegnummern laufen noch im alten Format. Bitte supabase/numbering_jjmm.sql in Supabase ausführen, damit die Nummern dem Projektnummern-Schema (z. B. RE-26081001) folgen.';

/**
 * Erkennt genau den Fall "diese Funktionssignatur gibt es (noch) nicht".
 *
 * Absichtlich eng: ein Rechtefehler, ein gesperrter Zaehler oder ein
 * Netzwerkabbruch duerfen NICHT in den Wiederholungspfad laufen — der
 * einargumentige Aufruf wuerde dann dieselbe Ablehnung erneut auf den Weg
 * bringen und der eigentliche Fehler waere verschleiert.
 */
const isUnknownSignature = (error: unknown): boolean => {
    const e = error as { code?: string; message?: string } | null;
    if (!e) return false;
    if (e.code === 'PGRST202') return true;
    const msg = (e.message || '').toLowerCase();
    return (
        msg.includes('could not find the function') ||
        (msg.includes('next_document_number') && msg.includes('does not exist'))
    );
};

/**
 * Zieht eine Nummer der Art `kind` im Kreis der Typziffer `typeDigit`.
 *
 * Die Typziffer ist fuer 'AB' bedeutungslos (Bestandspfad mit globalem
 * Zaehler); sie wird trotzdem mitgeschickt, damit alle Aufrufer denselben Weg
 * nehmen und der Rueckfall nur an einer Stelle steht.
 */
export const nextDocumentNumber = async (
    kind: string,
    typeDigit: TypeDigit = '1',
): Promise<string> => {
    const { data, error } = await supabase.rpc('next_document_number', {
        p_kind: kind,
        p_type_digit: typeDigit,
    });
    if (!error) return data as string;
    if (!isUnknownSignature(error)) throw error;

    console.warn(HINT, error);
    const { data: legacy, error: legacyError } = await supabase.rpc('next_document_number', {
        p_kind: kind,
    });
    if (legacyError) throw legacyError;
    return legacy as string;
};
